import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { PaginatedResponse } from '../../../core/models/paginated-response.model';
import { environment } from '../../../environments/environment';
import { Comment, Task } from '../models/tasks.model';
import { TaskStatus } from '../../../core/models/crm-types';
import { BranchContextService } from '../../../core/services/branch-context.service';

export type TaskPipelineResponse = {
  [K in TaskStatus]?: Task[];
};

@Injectable({
  providedIn: 'root'
})
export class TaskService {
  private http = inject(HttpClient);
  private branchContext = inject(BranchContextService);
  private apiUrl = `${environment.apiUrl}/tasks`;

  // A flexible method to get tasks with various filters
  getTasks(
    page: number = 1,
    limit: number = 10,
    filter: {
      status?: string;
      assigneeId?: string;
      branchId?: string | null;
      search?: string;
      priority?: string;
      entityType?: string;
      startDate?: string;
      endDate?: string;
      sortBy?: string;
      sortOrder?: string;
    }
  ): Observable<PaginatedResponse<Task>> {

    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    // Track if branchId was explicitly provided in filter
    const hasBranchIdInFilter = 'branchId' in filter;

    // Add filters if they are provided
    for (const key in filter) {
      if (Object.prototype.hasOwnProperty.call(filter, key)) {
        const value = (filter as any)[key];

        // Skip branchId in the loop - we'll handle it separately
        if (key === 'branchId') continue;

        // Add non-null, non-undefined values
        if (value !== null && value !== undefined && value !== '') {
          params = params.set(key, value);
        }
      }
    }

    // Handle branchId separately with proper null handling
    if (hasBranchIdInFilter) {
      // branchId was explicitly provided in filter
      if (filter.branchId !== null && filter.branchId !== undefined && filter.branchId !== '') {
        // Specific branch selected
        params = params.set('branchId', filter.branchId);
      }
      // If filter.branchId is null, it means "All branches" - don't add parameter
    } else {
      // branchId was not in filter, use context as fallback
      if (this.branchContext.selectedBranchId()) {
        params = params.set("branchId", this.branchContext.selectedBranchId()!);
      }
    }

    return this.http.get<PaginatedResponse<Task>>(this.apiUrl, { params });
  }

  getTaskById(id: string): Observable<Task> {
    return this.http.get<Task>(`${this.apiUrl}/${id}`);
  }

  createTask(taskData: any): Observable<any>;
  createTask(taskData: FormData): Observable<any>;
  createTask(taskData: any): Observable<any> {
    return this.http.post(this.apiUrl, taskData);
  }

  updateTask(id: string, updates: any): Observable<any>;
  updateTask(id: string, updates: FormData): Observable<any>;
  updateTask(id: string, updates: any): Observable<any> {
    return this.http.patch(`${this.apiUrl}/${id}`, updates);
  }

  // Soft delete by setting status to 'CANCELLED'
  deleteTask(id: string): Observable<Task> {
    return this.updateTask(id, { status: 'CANCELLED' });
  }

  getTaskPipeline(filters?: {
    search?: string;
    status?: string;
    priority?: string;
    assigneeId?: string;
    entityType?: string;
    startDate?: string;
    endDate?: string;
  }): Observable<TaskPipelineResponse> {
    let params = new HttpParams();

    // Add all filter parameters dynamically
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          params = params.set(key, value);
        }
      });
    }

    // Always add branch context if available
    if (this.branchContext.selectedBranchId()) {
      params = params.set('branchId', this.branchContext.selectedBranchId()!);
    }

    return this.http.get<TaskPipelineResponse>(`${this.apiUrl}/pipeline`, { params });
  }

  exportTasks(filters?: {
    search?: string;
    status?: string;
    priority?: string;
    assigneeId?: string;
    entityType?: string;
    startDate?: string;
    endDate?: string;
  }): Observable<any> {
    let params = new HttpParams();

    // Add all filter parameters dynamically
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          params = params.set(key, value);
        }
      });
    }

    params = params.set('format', 'csv');

    // Always add branch context if available
    if (this.branchContext.selectedBranchId()) {
      params = params.set('branchId', this.branchContext.selectedBranchId()!);
    }

    return this.http.get(`${this.apiUrl}/pipeline`, {
      params,
      responseType: 'blob'
    });
  }

  getTaskComments(id: string): Observable<Comment[]> {
    return this.http.get<Comment[]>(`${this.apiUrl}/tasks/${id}/comments`);
  }

  createComment(id: string, commentData: FormData): Observable<Comment> {
    return this.http.post<Comment>(`${this.apiUrl}/tasks/${id}/comments`, commentData);
  }

  updateComment(id: string, commentData: FormData): Observable<Comment> {
    return this.http.patch<Comment>(`${this.apiUrl}/comments/${id}`, commentData);
  }

  deleteComment(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/comments/${id}`);
  }

  nudgeAssignee(id: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/${id}/nudge`, null);
  }

  updateRecurringTask(id: string, data: any): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/recurring-tasks/${id}`, data);
  }

  deleteRecurringTask(id: string): Observable<any> {
    return this.http.delete(`${environment.apiUrl}/recurring-tasks/${id}`);
  }
}